import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class10GuidedPractice02 = () => {
  const navigate = useNavigate();

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>

          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>

          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">The Forgotten Forest</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                Found only in the Deep South of America, longleaf pine woodlands have dwindled to about 3 percent of their former range, but new efforts are under way to restore them.
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  The beauty and the biodiversity of the longleaf pine forest are well-kept secrets, even in its native South. Yet it is among the richest ecosystems in North America, rivalling tallgrass prairies and the ancient forests of the Pacific Northwest in the number of species it shelters. And like those two other disappearing wildlife habitats, longleaf is also critically endangered.
                </p>
                <p>
                  In longleaf pine forests, trees grow widely scattered, creating an open, parklike environment, more like a savanna than a forest. The trees are not so dense as to block the sun. This openness creates a forest floor that is among the most diverse in the world, where plants such as many-flowered grass pinks, trumpet pitcher plants, Venus flytraps, lavender ladies and pineland bog-buttons grow. As many as 50 different species of wildflowers, shrubs, grasses and ferns have been catalogued in just a single square meter.
                </p>
                <p>
                  Once, nearly 92 million acres of longleaf forest flourished from Virginia to Texas, the only place in the world where it is found. By the turn of the 21st century, however, virtually all of it had been logged, paved or farmed into oblivion. Only about 3 percent of the original range still supports longleaf forest, and only about 10,000 acres of that is uncut old-growth—the rest is forest that has regrown after cutting. An estimated 100,000 of those acres are still vanishing every year. However, a quiet movement to reverse this trend is rippling across the region. Governments, private organisations (including NWF) and individual conservationists are looking for ways to protect and preserve the remaining longleaf and to plant new forests for future generations.
                </p>
                <p>
                  Figuring out how to bring back the piney woods also will allow biologists to help the plants and animals that depend on this habitat. Nearly two-thirds of the declining, threatened or endangered species in the southeastern United States are associated with longleaf. The outright destruction of longleaf is only part of their story, says Mark Danaher, the biologist for South Carolina’s Francis Marion National Forest. He says the demise of these animals and plants also is tied to a lack of fire, which once swept through the southern forests on a regular basis. “Fire is absolutely critical for this ecosystem and for the species that depend on it,” says Danaher.
                </p>
                <p>
                  Name just about any species that occurs in longleaf and you can find a connection to fire. Bachman’s sparrow is a secretive bird with a beautiful song that echoes across the longleaf flatwoods. It tucks its nest on the ground beneath clumps of wiregrass and little bluestem in the open under-story. But once fire has been absent for several years, and a tangle of shrubs starts to grow, the sparrows disappear. Gopher tortoises, the only native land tortoises east of the Mississippi, are also abundant in longleaf. A keystone species for these forests, its burrows provide homes and safety to more than 300 species of vertebrates and invertebrates ranging from eastern diamond-back rattlesnakes to gopher frogs. If fire is suppressed, however, the tortoises are choked out. “If we lose fire,” says Bob Mitchell, an ecologist at the Jones Centre, “we lose wildlife.”
                </p>
                <p>
                  Without fire, we also lose longleaf. Fire knocks back the oaks and other hardwoods that can grow up to overwhelm longleaf forests. “They are fire forests,” Mitchell says. “They evolved in the lightning capital of the eastern United States.” And it wasn’t only lightning strikes that set the forest aflame. “Native Americans also lit fires to keep the forest open,” Mitchell says. “So did the early pioneers. They helped create the longleaf pine forests that we know today.”
                </p>
                <p>
                  Fire also changes how nutrients flow throughout longleaf ecosystems, in ways we are just beginning to understand. For example, researchers have discovered that frequent fires provide extra calcium, which is critical for egg production, to endangered red-cockaded woodpeckers. Frances James, a retired avian ecologist from Florida State University, has studied these small black-and-white birds for more than two decades in Florida’s sprawling Apalachicola National Forest. When she realised female woodpeckers laid larger clutches in the first breeding season after their territories were burned, she and her colleagues went searching for answers. “We learned calcium is stashed away in woody shrubs when the forest is not burned,” James says. “But when there is a fire, a pulse of calcium moves down into the soil and up into the longleaf.” Eventually, this calcium makes its way up the food chain to a tree-dwelling species of ant, which is the red-cockade’s favourite food. The result: more calcium for the birds, which leads to more eggs, younger and more woodpeckers.
                </p>
                <p>
                  Today, fire is used as a vital management tool for preserving both longleaf and its wildlife. Most of these fires are prescribed burns, deliberately set with a drip torch. Although the public often opposes any type of fire—and the smoke that goes with it—these frequent, low-intensity burns reduce the risk of catastrophic conflagrations. “Forests are going to burn,” says Amadou Diop, NWF’s southern forests restoration manager. “It’s just a question of when. With prescribed burns, we can pick the time and the place.”
                </p>
                <p>
                  Diop is spearheading a new NWF effort to restore longleaf. “It’s a species we need to go back to,” he says. Educating landowners about the advantages of growing longleaf is part of the program, he adds, which will soon be under way in nine southern states. “Right now, most longleaf is on public land,” says Jerry McCollum, president of the Georgia Wildlife Federation. “Private land is where we need to work,” he adds, pointing out that more than 90 percent of the acreage within the historic range of longleaf falls under this category.
                </p>
                <p>
                  Interest among private landowners is growing throughout the South, but restoring longleaf is not an easy task. The herbaceous layer—the understory of wiregrasses and other plants – also needs to be re-created. In areas where the land has not been chewed up by farming, but converted to loblolly or slash pine plantations, the seed bank of the longleaf forest usually remains viable beneath the soil. In time, this original vegetation can be coaxed back. Where agriculture has destroyed the seeds, however, wiregrass must be replanted. Right now, the expense is prohibitive, but researchers are searching for low-cost solutions.
                </p>
                <p>
                  Bringing back longleaf is not for the short-sighted, however. Few of us will be alive when the pines being planted today become mature forests in 70 to 80 years. But that is not stopping longleaf enthusiasts. “Today, it’s getting hard to find longleaf seedlings to buy,” one of the private landowners says. “Everyone wants them. Longleaf is in a resurgence.”
                </p>
              </div>
            </div>
          </div>






          {/* Right: Flowchart Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }} // Optional: prevents jumping
          >
            {/* Questions Header */}
            <div className="mb-6 w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">Questions 6 – 9</h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the flow chart below. <br />
                Write <strong>ONE WORD ONLY</strong> from the passage for each answer.
              </p>
            </div>

            {/* Flowchart */}
            <div className="flex flex-col items-center text-[14px] text-black space-y-3 w-full">
              {/* Step 1 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Calcium stored in
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[5] = el}
                    type="text"
                    placeholder="6"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[5] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[5] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 2 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Shrubs are burned
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 3 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Calcium released into
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[6] = el}
                    type="text"
                    placeholder="7"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[6] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[6] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 4 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Travel up to the leaves
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 5 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[7] = el}
                    type="text"
                    placeholder="8"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[7] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[7] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                are eaten
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 6 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Number of
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[8] = el}
                    type="text"
                    placeholder="9"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[8] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[8] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                increases
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 7 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                More cockaded woodpeckers
              </div>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-2xl">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>
          {/* Numbers with lines above */}
          <div className="flex items-end space-x-2">
            {[6, 7, 8, 9].map((num) => {
              const isActive = currentQuestion === num;
              const isAnswered = answers[num - 1] && answers[num - 1] !== '';
              return (
                <div key={num} className="flex flex-col items-center">
                  {/* Line above number */}
                  <div
                    className="h-1 w-7 mb-1 rounded-full"
                    style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                  ></div>

                  {/* Number button */}
                  <button
                    onClick={() => {
                      setCurrentQuestion(num);
                      const input = questionRefs.current[num - 1];
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }}
                    className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
                ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
                transition-colors duration-150`}
                    style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                  >
                    {num}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>








    </div>
  );
};

export default Class10GuidedPractice02;
